import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Divider,
  Grid,
  Container
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '../contexts/AuthContext';
import { loginUser } from '../data/userApi';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | React.ReactNode | null>(null);

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { isAuthenticated, isVerified, login, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the return URL from location state or default to home page
  const from = (location.state as any)?.from?.pathname || '/';


  const formErrorId = 'form-error';

  // Display auth context error if present (e.g., verification required)
  useEffect(() => {
    if (authError) {
      setError(
        <Box>
          <Typography variant="body2" gutterBottom>
            {authError}
          </Typography>
          {authError.includes('verify') && (
            <Button
              size="small"
              color="primary"
              component={RouterLink}
              to={`/verify-email?email=${encodeURIComponent(email)}`}
              sx={{ mt: 1 }}
            >
              Resend verification email
            </Button>
          )}
        </Box>
      );
    }
  }, [authError, email]);

  // If already authenticated and verified, redirect to the return URL
  useEffect(() => {
    if (isAuthenticated && isVerified) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isVerified, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tokenResponse = await loginUser({ email, password });
      await login(tokenResponse);
      // Navigation is now handled by the useEffect when isAuthenticated and isVerified change
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.message === 'LOGIN_BAD_CREDENTIALS') {
        setError('Invalid email or password');
      } else if (err.message === 'LOGIN_USER_NOT_VERIFIED') {
        setError(
          <Box>
            <Typography variant="body2" gutterBottom>
              Please verify your email before logging in.
            </Typography>
            <Button
              size="small"
              color="primary"
              component={RouterLink}
              to={`/verify-email?email=${encodeURIComponent(email)}`}
              sx={{ mt: 1 }}
            >
              Resend verification email
            </Button>
          </Box>
        );
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ p: 4 }}
        role="main"
        aria-labelledby="login-title"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <LoginIcon color="primary" sx={{ fontSize: 40, mb: 2 }} aria-hidden="true" />
          <Typography variant="h4" component="h1" id="login-title" gutterBottom>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Sign in to your account to shop and track orders
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            id={formErrorId}
            role="alert"
          >
            {error}
          </Alert>
        )}

        <form 
          onSubmit={handleSubmit}
          noValidate
          aria-describedby={error ? formErrorId : undefined}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                fullWidth
                label="Email" 
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                disabled={loading}
                autoFocus
                required
                error={false}
                helperText=""
                id="email"
                name="email"
                autoComplete="email"
                inputProps={{
                  'aria-describedby': undefined,
                }}
                aria-required="true"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField 
                fullWidth
                label="Password" 
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                disabled={loading}
                required
                error={false}
                helperText=""
                id="password"
                name="password"
                autoComplete="current-password"
                inputProps={{
                  'aria-describedby': undefined,
                }}
                aria-required="true"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={togglePasswordVisibility}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff aria-hidden="true" /> : <Visibility aria-hidden="true" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          
          <Button 
            type="submit" 
            fullWidth 
            variant="contained" 
            color="primary"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
            aria-busy={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
          
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Link 
              component={RouterLink} 
              to="/forgot-password" 
              underline="hover" 
              color="primary" 
              variant="body2"
              aria-label="Forgot your password? Reset it here"
            >
              Forgot password?
            </Link>
          </Box>
        </form>
        
        <Divider sx={{ my: 3 }} role="presentation">
          <Typography variant="body2" color="text.secondary">
            New to our store?
          </Typography>
        </Divider>
        
        <Button
          component={RouterLink}
          to="/signup"
          fullWidth
          variant="outlined"
          color="primary"
          sx={{ mt: 1 }}
          aria-label="Create a new account"
        >
          Create Account
        </Button>
      </Paper>
    </Container>
  );
};

export default Login; 